import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Users,
  DollarSign,
  TrendingDown,
  AlertTriangle,
  Activity,
  RefreshCw,
  ArrowRight,
  CreditCard,
  BarChart3,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const KPICard = ({ icon: Icon, label, value, subtext, color, trend }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold tracking-tight">{value}</p>
          {subtext && (
            <p className={`text-xs ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
              {subtext}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color || 'bg-primary/10'}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AdminSubscriptionDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminSubscriptionApi.getDashboard();
      setData(res);
      setLastRefresh(new Date());
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Subscription Dashboard</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-8 w-16 bg-muted rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const revenueByPlan = data?.revenue_by_plan || [];
  const recentActivity = data?.recent_activity || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Subscription Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Overview of subscription metrics and activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground">
              Updated {formatDate(lastRefresh)}
            </span>
          )}
          <button
            onClick={fetchDashboard}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Users}
          label="Active Subscribers"
          value={kpis.active_subscribers?.toLocaleString() || '0'}
          subtext={kpis.subscriber_change ? `${kpis.subscriber_change > 0 ? '+' : ''}${kpis.subscriber_change} this month` : null}
          trend={kpis.subscriber_change > 0 ? 'up' : 'down'}
          color="bg-blue-600"
        />
        <KPICard
          icon={DollarSign}
          label="Monthly Recurring Revenue"
          value={formatCurrency(kpis.mrr)}
          subtext={kpis.mrr_change ? `${kpis.mrr_change > 0 ? '+' : ''}${kpis.mrr_change}% vs last month` : null}
          trend={kpis.mrr_change > 0 ? 'up' : 'down'}
          color="bg-green-600"
        />
        <KPICard
          icon={TrendingDown}
          label="Churn Rate (30d)"
          value={`${(kpis.churn_rate_30d || 0).toFixed(1)}%`}
          subtext={kpis.churn_trend || null}
          trend="down"
          color="bg-orange-500"
        />
        <KPICard
          icon={AlertTriangle}
          label="Failed Payments"
          value={kpis.failed_payments?.toString() || '0'}
          subtext={kpis.failed_amount ? formatCurrency(kpis.failed_amount) + ' at risk' : null}
          color="bg-red-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue by Plan */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Revenue by Plan
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {revenueByPlan.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No plan data available</p>
            ) : (
              <div className="space-y-3">
                {revenueByPlan.map((plan) => {
                  const maxRevenue = Math.max(...revenueByPlan.map((p) => p.revenue || 0), 1);
                  const pct = ((plan.revenue || 0) / maxRevenue) * 100;
                  return (
                    <div key={plan.slug || plan.name} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{plan.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {plan.subscriber_count || 0} subs
                          </Badge>
                        </div>
                        <span className="font-semibold">{formatCurrency(plan.revenue)}</span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.slice(0, 10).map((event, i) => (
                  <div key={event.id || i} className="flex items-start gap-3 text-sm">
                    <div className={`mt-0.5 p-1 rounded-full ${
                      event.type === 'subscription' ? 'bg-blue-100 text-blue-600' :
                      event.type === 'payment' ? 'bg-green-100 text-green-600' :
                      event.type === 'cancellation' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {event.type === 'payment' ? <CreditCard className="h-3 w-3" /> :
                       event.type === 'cancellation' ? <TrendingDown className="h-3 w-3" /> :
                       <Activity className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{event.description}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(event.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Manage Subscribers', path: '/admin/subscriptions/subscribers', icon: Users },
              { label: 'View Transactions', path: '/admin/subscriptions/transactions', icon: CreditCard },
              { label: 'Webhook Events', path: '/admin/subscriptions/webhooks', icon: Activity },
              { label: 'Manage Plans', path: '/admin/subscriptions/plans', icon: BarChart3 },
              { label: 'Audit Log', path: '/admin/subscriptions/audit-log', icon: Clock },
            ].map((action) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <action.icon className="h-4 w-4" />
                {action.label}
                <ArrowRight className="h-3 w-3 ml-1 opacity-50" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
